import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from '../../../AppContext';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import mainAxios from '../../../services/mainAxios';
import debounce from 'lodash/debounce';

const useNotifications = () => {
  const { user: [user] } = useAppContext();
  const { notificationsRefresh: [notificationsRefresh] } = useAppContext();
  const { t } = useTranslation("translations");

  const initialTableRows = {
    rowsPerPage: 10,
    currentPage: 1,
    totalSize: 0,
    hasMore: true
  };

  const notificationTypes = useMemo(() => ({
    all: { value: null, label: "All", notifications: [] },
    SECURITY_AND_LOGGINS: { value: "SECURITY_AND_LOGGINS", label: "Security", notifications: [] },
    SUBSCRIPTION_AND_CERTIFICATE: { value: "SUBSCRIPTION_AND_CERTIFICATE", label: "Subscription", notifications: [] },
    SYSTEM_NOTIFICATIONS: { value: "SYSTEM_NOTIFICATIONS", label: "System", notifications: [] },
    MY_PURCHASE: { value: "MY_PURCHASE", label: "Purchase", notifications: [] },
    FISCALIZED_INVOICES: { value: "FISCALIZED_INVOICES", label: "Fiscalized Invoices", notifications: [] },
    OPERATIONS_REPORTING: { value: "OPERATIONS_REPORTING", label: "Operations Report", notifications: [] },
  }), []);

  const [tableRows, setTableRows] = useState(initialTableRows);
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({
    ERRORS: 0,
    GENERAL: 0,
    CIS: 0,
    INFO: 0,
    WARNING: 0
  });

  const defaultParams = { read: "false", type: notificationTypes.all.value };
  const [params, setParams] = useState(defaultParams);
  const [loading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNotificationIndex, setExpandedNotificationIndex] = useState(null);
  const notificationListRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const markReadDef = {
    ERRORS: false,
    GENERAL: false,
    CIS: false,
    INFO: false,
    WARNING: false
  };
  const [markRead, setMarkRead] = useState(markReadDef);

  const getNotifications = useCallback(async (reset = false) => {
    if (loading || (!reset && !tableRows.hasMore)) return;

    if (notificationListRef.current && !reset) {
      scrollPositionRef.current = notificationListRef.current.scrollTop;
    }

    setIsLoading(true);
    const pageToLoad = reset ? 1 : tableRows.currentPage;

    try {
      const res = await mainAxios.post("/apiEndpoint/search", {
        object: "GetUserNotificationView",
        params: {
          ...params,
          username: user.username
        },
        pagination: {
          pageNumber: pageToLoad,
          pageSize: tableRows.rowsPerPage
        },
        sortCriteria: [{
          field: "timestamp",
          direction: "DESC"
        }],
      });

      if (res.data?.status) {
        const newNotifications = res.data.content ?? [];
        const totalSize = res.data.totalSize || 0;

        setNotifications(prev => reset ? newNotifications : [...prev, ...newNotifications]);
        setTableRows(prev => ({
          ...prev,
          currentPage: pageToLoad + 1,
          totalSize,
          hasMore: (prev.rowsPerPage * pageToLoad) < totalSize
        }));
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [params, tableRows.rowsPerPage, tableRows.currentPage, tableRows.hasMore, loading]);

  const getNotificationCounts = useCallback(async () => {
    try {
      const res = await mainAxios.post("/apiEndpoint/search", {
        object: "GetUserNotificationCounts"
      });
      if (res.data?.status && res.data.content?.length > 0) {
        setNotificationCounts(res.data.content[0]);
      }
    } catch (error) {
      console.error("Error fetching notification counts:", error);
    }
  }, []);

  const markAllAsRead = useCallback(async (type) => {
    try {
      await mainAxios.post("/api/notifications/mark-all-as-read", {
        type
      });
      await getNotificationCounts();
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  }, [getNotificationCounts]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      setTableRows(initialTableRows);
      setNotifications([]);
      scrollPositionRef.current = 0;
      await getNotifications(true);
    };
    fetchData();
  }, [isOpen, params, notificationsRefresh]);

  useEffect(() => {
    getNotificationCounts();
  }, [notificationsRefresh]);

  const handleScroll = useCallback(debounce(() => {
    if (!notificationListRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = notificationListRef.current;
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100;

    if (isNearBottom && !loading && tableRows.hasMore) {
      getNotifications();
    }
  }, 100), [loading, tableRows.hasMore, getNotifications]);

  useEffect(() => {
    if (!isOpen || !notificationListRef.current) return;

    const listElement = notificationListRef.current;
    listElement.addEventListener('scroll', handleScroll);

    return () => {
      listElement.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [isOpen, handleScroll]);

  useEffect(() => {
    if (notificationListRef.current && !loading) {
      requestAnimationFrame(() => {
        if (notificationListRef.current) {
          notificationListRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [notifications, loading]);

  const parsedNotificationsArr = useMemo(() =>
    Object.values(notificationTypes), [notificationTypes]);

  const handleParamsChange = useCallback((key, value) =>
    setParams(prev => ({ ...prev, [key]: value })), []);

  const handleExpandClick = useCallback((index) => {
    setExpandedNotificationIndex(prev => prev === index ? null : index);
  }, []);

  const closeMenu = () => {
    setExpandedNotificationIndex(null);
    Object.keys(markRead).forEach(type => {
      const isRead = markRead[type];
      if (isRead && notificationCounts[type] > 0) {
        markAllAsRead(type);
      }
    });
    setMarkRead(markReadDef);
    setIsOpen(false);
  };

  const onOpen = () => {
    setMarkRead(prev => ({
      ...prev,
      [notificationTypes.all.value]: true
    }));
    setIsOpen(true);
  };

  const totalUnread = useMemo(() => {
    return Object.entries(notificationCounts).reduce((acc, [key, count]) => {
      if (!markRead[key]) {
        return acc + count;
      }
      return acc;
    }, 0);
  }, [notificationCounts, markRead]);

  const getNotificationGroup = useCallback((timestamp) => {
    const now = moment();
    const notificationDate = moment(timestamp);

    if (notificationDate.isSame(now, 'day')) {
      return t("common.today");
    }
    if (notificationDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return t("common.yesterday");
    }
    if (notificationDate.isAfter(now.clone().startOf('week').subtract(1, 'day'))) {
      return t("common.thisWeek");
    }
    return t("common.older");
  }, [t]);

  return {
    notifications,
    notificationCounts,
    params,
    loading,
    isOpen,
    setIsOpen,
    expandedNotificationIndex,
    totalUnread,
    parsedNotificationsArr,
    notificationListRef,
    scrollPositionRef,
    notificationTypes,
    markRead,
    setMarkRead,
    handleParamsChange,
    handleExpandClick,
    closeMenu,
    onOpen,
    getNotificationGroup,
    t
  };
};

export default useNotifications;
